import React, { useRef, useMemo, useState, useCallback } from 'react';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
// @ts-ignore
import icon from 'leaflet/dist/images/marker-icon.png';
// @ts-ignore
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
// @ts-ignore
import iconRetina from 'leaflet/dist/images/marker-icon-2x.png';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import L, { LatLngBoundsExpression, MarkerCluster } from 'leaflet';
import { MapContainer, TileLayer, Rectangle } from 'react-leaflet';
import MapLegendControl from './MapLegend';
import { BaseProperty } from '@/features/property/types';
import { Competitor } from '@/features/compset/types';
import { MapEvents } from './MapEvents';
import { FitBounds } from './FitBounds';
import { PropertyMarker } from './PropertyMarker';
import { PROPERTY_COLORS } from '@/features/property/utils/consts';
import { NavigateToProperty } from '@/features/property/components/NavigateToProperty';

// Fix Leaflet's default icon issue - MUST be done before any Leaflet component renders
// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: iconRetina,
  iconUrl: icon,
  shadowUrl: iconShadow,
});

interface PropertyMapProps {
  ownProperty: BaseProperty;
  competitors: Competitor[];
  marketProperties: BaseProperty[];
  onPropertyClick?: (property: BaseProperty | Competitor) => void;
  selectedPropertyId?: number;
}

const PropertyMap: React.FC<PropertyMapProps> = React.memo(({
  ownProperty,
  competitors,
  marketProperties,
  onPropertyClick,
  selectedPropertyId
}) => {
  const default_latlong: [number, number] = [
    ownProperty.latitude ?? 0,
    ownProperty.longitude ?? 0
  ];
  const allProperties = useMemo(() => [ownProperty, ...competitors, ...marketProperties], [ownProperty, competitors, marketProperties]);
  const [center, setCenter] = useState<[number, number]>(default_latlong);
  const [zoom, setZoom] = useState<number>(0);

  const boundingBox = useMemo(() => {
    if (allProperties.length === 0) return null;

    // Initialize with first property
    let minLat = allProperties[0].latitude;
    let maxLat = allProperties[0].latitude;
    let minLng = allProperties[0].longitude;
    let maxLng = allProperties[0].longitude;

    // Find min/max coordinates
    allProperties.forEach(property => {
      minLat = Math.min(minLat, property.latitude);
      maxLat = Math.max(maxLat, property.latitude);
      minLng = Math.min(minLng, property.longitude);
      maxLng = Math.max(maxLng, property.longitude);
    });

    // Add some padding (5%)
    const latPadding = (maxLat - minLat) * 0.05;
    const lngPadding = (maxLng - minLng) * 0.05;

    return [
      [minLat - latPadding, minLng - lngPadding],
      [maxLat + latPadding, maxLng + lngPadding]
    ] as LatLngBoundsExpression;
  }, [allProperties]);

  const onCenterChange = useCallback((center: [number, number]): void => {
    setCenter(prevCenter => {
      if (Math.abs(prevCenter[0] - center[0]) > 0.0001 ||
        Math.abs(prevCenter[1] - center[1]) > 0.0001) {
        return center;
      }
      return prevCenter;
    });
  }, []);

  const onZoomChange = useCallback((zoom: number): void => {
    setZoom(prevZoom => prevZoom !== zoom ? zoom : prevZoom);
  }, []);

  const marketPropertyMarkers = useMemo(() => {
    return marketProperties.map(property => (
      <PropertyMarker
        key={`market-${property.property_id}`}
        property={property}
        onPropertyClick={onPropertyClick}
        isSelected={selectedPropertyId === property.property_id}
        type="market"
      />
    ));
  }, [marketProperties, onPropertyClick]);

  const competitorPropertyMarkers = useMemo(() => {
    return competitors.map(property => (
      <PropertyMarker
        key={`competitor-${property.property_id}`}
        property={property}
        onPropertyClick={onPropertyClick}
        isSelected={selectedPropertyId === property.property_id}
        type="competitor"
      />
    ));
  }, [competitors, onPropertyClick]);

  return (
    <div className="h-full w-full rounded-lg overflow-hidden border border-gray-200">
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
        className="h-full w-full"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, &copy; <a href="https://carto.com/attributions">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png"
        />

        <MapEvents
          onCenterChange={onCenterChange}
          onZoomChange={onZoomChange}
        />

        {boundingBox && (
          <FitBounds
            bounds={boundingBox}
            onCenterChange={onCenterChange}
            onZoomChange={onZoomChange}
          />
        )}

        {boundingBox && (
          <Rectangle
            bounds={boundingBox}
            pathOptions={{
              color: '#10B981',
              weight: 2,
              fillColor: '#10B981',
              fillOpacity: 0.1
            }}
          />
        )}

        <NavigateToProperty
          properties={allProperties}
          selectedPropertyId={selectedPropertyId}
        />

        <MapLegendControl />

        <MarkerClusterGroup
          maxClusterRadius={30}
          chunkedLoading={true}
          zIndexOffset={-1000}
          iconCreateFunction={(cluster: MarkerCluster) => {
            return L.divIcon({
              html: `<div class="${PROPERTY_COLORS.market.bg} text-white rounded-full flex items-center justify-center font-bold" style="width: 30px; height: 30px;">${cluster.getChildCount()}</div>`,
              className: 'market-cluster',
              iconSize: L.point(30, 30)
            });
          }}
        >
          {marketPropertyMarkers}
        </MarkerClusterGroup>

        {/* Competitor properties in their own cluster group - middle z-index */}
        <MarkerClusterGroup
          maxClusterRadius={25}
          chunkedLoading={true}
          zIndexOffset={0}
          iconCreateFunction={(cluster: MarkerCluster) => {
            return L.divIcon({
              html: `<div class="${PROPERTY_COLORS.competitor.bg} text-white rounded-full flex items-center justify-center font-bold" style="width: 35px; height: 35px;">${cluster.getChildCount()}</div>`,
              className: 'competitor-cluster',
              iconSize: L.point(35, 35)
            });
          }}
        >
          {competitorPropertyMarkers}
        </MarkerClusterGroup>

        {/* Own property not in a cluster - highest z-index */}
        <PropertyMarker
          property={ownProperty}
          onPropertyClick={onPropertyClick}
          isSelected={selectedPropertyId === ownProperty.property_id}
          type="ownProperty"
        />
      </MapContainer>
    </div>
  );
});

PropertyMap.displayName = 'PropertyMap';

export default PropertyMap;